

























































































































































































































































































































































































































































































































































































































































import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator';

import { RobotOption } from '@/models/MetaTags';
import { BusyObject, BusyList } from '@/models/Busy';
import { Account, AccountAddress, AccountsService, AddressesService, MarketplacePurchase, Package, PackagesService, Subscription, SubscriptionsService } from '@/api/braendz';
import { AccountInfo } from '@azure/msal-browser';

import LoadingView from '@/components/Spinner/LoadingView.vue';
import TermsPopup from '@/components/Popups/TermsPopup.vue';
import ProductCardStarter from '@/components/Products/ProductCardStarter.vue';
import ProductCardProfessional from '@/components/Products/ProductCardProfessional.vue';
import ProductCardEnterprise from '@/components/Products/ProductCardEnterprise.vue';
import ProductCardProfessionalPromo1 from '@/components/Products/ProductCardProfessionalPromo1.vue';

@Component({
    components: {
        LoadingView,
        TermsPopup,
        ProductCardStarter,
        ProductCardProfessional,
        ProductCardProfessionalPromo1,
        ProductCardEnterprise
    },
    metaInfo() {
        return {
            title: this.$i18n.t("subscribeMarketplace.title").toString(),
            meta: [
                { name: 'robots', content: [RobotOption.NoIndex, RobotOption.NoFollow].join(',') }
            ]
        };
    }
})
export default class SubscribeMarketplace extends Vue {
    // Member:
    public step = 1;
    public termsVisible = false;

    public marketplacePurchase: BusyObject<MarketplacePurchase> = new BusyObject<MarketplacePurchase>();
    public packages = new BusyList<Package>();
    public selectedPackages: Package[] = [];

    public billingAddress = new BusyObject<AccountAddress>({});
    public billingAddressValid = false;

    public updatedAccount = new BusyObject<Account>();

    public termsAgreed = false;
    public withdrawalAgreed = false;
    public marketingAgreed = false;
    public isBusinessAccount = false;

    public activatedSubscription = new BusyObject<Subscription>();

    public activationResult: 'WaitingForActivation' | 'Succeeded' | 'UnexpectedState' | 'Failed' | null = null;
    public waitingForActivationInterval: number | null = null;

    // Properties:

    // This is the token received from microsoft marketplace
    @Prop({ required: false, type: String, default: null })
    public token!: string | null;

    @Prop({ required: false, default: 1 })
    public initialStep!: number;

    @Prop({ required: false, type: Array, default: () => [] })
    public packageKeys!: string[];

    // Getter:
    public get isInitializing(): boolean {
        return !this.marketplacePurchase.object || this.marketplacePurchase.isBusy;
    }

    public get userAccount(): AccountInfo | null {
        return this.$store.state.userAccount;
    }

    public get activeSubscription(): BusyObject<Subscription> {
        return this.$store.state.activeSubscription;
    }

    public get hasPurchasedSubscription(): boolean {
        return !!this.activeSubscription.object &&
               this.activeSubscription.object.state === 'Active' &&
               !!this.activeSubscription.object.product &&
               this.activeSubscription.object.product.action === 'Purchase';
    }

    public get account(): BusyObject<Account> {
        return this.$store.state.account;
    }

    public get numberOfMissingPackages(): number | null {
        if (this.marketplacePurchase.object?.product?.includedPackages) {
            return this.marketplacePurchase.object?.product?.includedPackages - this.selectedPackages.length;
        }
        return null;
    }

    public get recommendationEuropeanUnionActive() {
        return this.selectedPackages.find(pgk => pgk.key === 'germany' || pgk.key === 'austria' || pgk.key === 'france' || pgk.key === 'spain')
            && !this.selectedPackages.find(pgk => pgk.key === 'europe');
    }

    public get configurationCompleted(): boolean {
        const numberOfMissingPackages = this.numberOfMissingPackages;
        return !this.hasPurchasedSubscription &&
            (numberOfMissingPackages === null ? true : numberOfMissingPackages <= 0) &&
            (this.isBusinessAccount ? this.billingAddressValid : true) &&
            this.termsAgreed && this.withdrawalAgreed;
    }

    // Watchers:
    @Watch('step')
    public onStepChange(newStep: number, oldStep: number) {
        if (newStep !== oldStep) {
            this.updateQueryParameterStep(newStep);
        }
    }

    @Watch('selectedPackages')
    public onSelectedPackagesChange(newPackages: Package[], oldPackages: Package[]) {
        if (newPackages !== oldPackages) {
            this.updateQueryParameterPackages(newPackages);
        }
    }

    // Lifecycle Methods:
    public mounted(): void {
        if (this.token) {
            this.resolveTokenAsync(this.token);
        }

        // Initialize packages:
        this.initPackages();

        // Initialize billing address:
        this.initBillingAddress();

        // Set the initial step:
        this.initStep();
    }

    public beforeDestroy(): void {
        this.stopWaitingForActivation();
    }

    // Methods
    public finishStepProductConfiguration() {
        this.step = 2;
    }

    public async finishStepBillingAddress() {
        if(this.isBusinessAccount) {
            await this.upsertBillingAddress();
        }
        this.step = 3;
    }

    public finishStepActivation(result: 'WaitingForActivation' | 'Succeeded' | 'UnexpectedState' | 'Failed' | null) {
        this.activationResult = result;
        this.step = 4;
    }

    public restart() {
        this.step = 1;
        this.activationResult = null;
        this.isBusinessAccount = false;
        this.termsAgreed = false;
        this.withdrawalAgreed = false;
        this.marketingAgreed = false;
    }

    public initStep() {
        this.step = this.initialStep;
    }

    public async initPackages() {
        const packages = await this.packages.load(async () => {
            return (await PackagesService.getPackages(false));
        });
        this.selectedPackages = (packages ?? []).filter(pkg => this.packageKeys.includes(pkg.key));
    }

    public async initBillingAddress() {
        if (this.userAccount) {
            await this.getBillingAddress();
        }
    }

    public async resolveTokenAsync(token: string): Promise<void> {
        await this.marketplacePurchase.load(async () => {
            return await SubscriptionsService.resolveMarketplaceToken(token);
        });
    }

    public isPackageSelectionDisabled(pkg: Package): boolean {
        if (this.selectedPackages.includes(pkg)) {
            return false;
        }
        else if (!(this.marketplacePurchase.object?.product?.includedPackages)) {
            return true;
        }
        else if (this.marketplacePurchase.object.product.includedPackages - this.selectedPackages.length <= 0) {
            return true;
        }
        else {
            return false;
        }
    }

    public async getBillingAddress() {
        await this.billingAddress.load(async () => {
            const result = await AddressesService.getBillingAddressCurrentUserDefaultAccount();
            return result ?? {};
        });
    }

    public async upsertBillingAddress() {
        await this.billingAddress.update(async () => {
            if (this.billingAddress.object) {
                return await AddressesService.upsertBillingAddressCurrentUserDefaultAccount(this.billingAddress.object);
            }
            else {
                return null;
            }
        });
    }

    public async updateAccount() {
        await this.updatedAccount.update(async () => {
            const currentAccount = this.account.object;

            if(!currentAccount || !currentAccount.id) {
                throw new Error("Account object is not available.");
            }

            // Update the user account with the new billing address:
            currentAccount.type = this.isBusinessAccount ? 'Business' : 'Consumer';
            currentAccount.agreedTermsUtc = this.termsAgreed ? new Date().toISOString() : null;
            currentAccount.agreedWithdrawalUtc = this.withdrawalAgreed ? new Date().toISOString() : null;
            currentAccount.agreedMarketingUtc = this.marketingAgreed ? new Date().toISOString() : null;

            // Update the user account in the backend:
            const updatedAccount = await AccountsService.updateAccount(currentAccount.id, currentAccount);

            // Update the user account in the store:
            this.$store.commit('setAccount', updatedAccount);

            return updatedAccount;
        });
    }

    public updateQueryParameterStep(step?: number) {
        const routerStep = this.$route.query.step;
        if (step?.toString() === routerStep) return;

        if (!step || isNaN(step) || step < 1) {
            const query = { ...this.$route.query };
            delete query.step;
            this.$router.replace({ query });
        }
        else {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    step: step.toString()
                }
            });
        }
    }

    public updateQueryParameterPackages(packages?: Package[]) {
        const packageKeys = packages?.map(p => p.key) ?? [];
        const routerPackageKeys = Array.isArray(this.$route.query.packages) ? this.$route.query.packages as string[]
            : this.$route.query.packages ? [this.$route.query.packages] : [];
    

        if(packageKeys.length === 0 && routerPackageKeys.length === 0) {
            // If no packages are selected and no router package keys are present, do nothing.
            return;
        }

        if(!packageKeys.find(p => !routerPackageKeys.includes(p)) &&
           !routerPackageKeys.find(p => !packageKeys.includes(p))) {
            // If the selected packages and router package keys are the same, do nothing.
            return;
        }

        // The parameter packages should be updated.
        if (!packages || packages.length === 0) {
            const query = { ...this.$route.query };
            delete query.packages;
            this.$router.replace({ query });
        }
        else {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    packages: packages.map(p => p.key)
                }
            });
        }
    }

    public async activateSubscription() {
        try {
            await this.updateAccount();
            const activatedSubscription = await this.activatedSubscription.create(async () => {
                if(!this.marketplacePurchase.object) {
                    throw new Error("Marketplace purchase object is not available.");
                }
                else if(!this.marketplacePurchase.object.id) {
                    throw new Error("Marketplace purchase ID is not available.");
                }
                else if(!this.marketplacePurchase.object.planId) {
                    throw new Error("Marketplace plan ID is not available.");
                }

                return await SubscriptionsService.activateMarketplaceSubscriptionCurrentUserDefaultAccount({
                    subscriptionId: this.marketplacePurchase.object.id,
                    marketplacePlanId: this.marketplacePurchase.object.planId,
                    packageKeys: this.selectedPackages.map(i => i.key),
                    validFromUtc: this.marketplacePurchase.object.subscription?.term?.startDate,
                    validUntilUtc: this.marketplacePurchase.object.subscription?.term?.endDate
                });
            });

            if(!activatedSubscription) {
                this.activatedSubscription.error = "Activated subscription is not available.";
                this.finishStepActivation('Failed');
                return;
            }

            this.updateVuexActiveSubscription(activatedSubscription);
            this.startWaitingForActivation();
        } 
        catch(error) {
            this.activatedSubscription.error = error instanceof Error ? error.message : error;
            this.finishStepActivation('Failed');
        }
    }

    public async startWaitingForActivation(): Promise<void> {
        try {
            this.finishStepActivation('WaitingForActivation');

            this.waitingForActivationInterval = window.setInterval(async() => {
                try {
                    const subscription = await this.activatedSubscription.load(async () => {
                        if(!this.activatedSubscription.object) {
                            throw new Error("Subscription is not available.");
                        }
                        else if(!this.activatedSubscription.object.id) {
                            throw new Error("Subscription ID is not available.");
                        }

                        return SubscriptionsService.getSubscription(this.activatedSubscription.object.id);
                    }, undefined, true);

                    if (subscription) {
                        this.updateVuexActiveSubscription(subscription);
                    }

                    if(subscription?.state === 'ApprovalPending') {
                        // If the subscription is still pending approval, continue waiting.
                        return;
                    }

                    if(subscription?.state === 'Active') {
                        this.finishStepActivation('Succeeded');
                    }
                    else if(subscription?.state) {
                        this.finishStepActivation('UnexpectedState');
                    }
                    else {
                        this.finishStepActivation('Failed');
                    }
                }
                catch(error) {
                    this.activatedSubscription.error = error instanceof Error ? error.message : error;
                    this.finishStepActivation('Failed');
                }

                this.stopWaitingForActivation();
            }, 10000);
        }
        catch(error) {
            this.activatedSubscription.error = error instanceof Error ? error.message : error;
            this.finishStepActivation('Failed');
        }
    }

    public stopWaitingForActivation(): void {
        if (this.waitingForActivationInterval) {
            clearInterval(this.waitingForActivationInterval);
            this.waitingForActivationInterval = null;
        }
    }

    public updateVuexActiveSubscription(activeSubscription: Subscription) {
        this.$store.commit('setActiveSubscription', activeSubscription);
    }

}
